import { RootChartConfigObject } from '../../types/shared'
import {
  LEGACY_HORIZONTAL_TOOLBAR_BREAK_POINT,
  LEGACY_VERTICAL_TOOLBAR_WIDTH_WITH_OFFSET,
  MARKET_END_MINUTES,
  MARKET_START_MINUTES,
  SpecificChartFunctionality,
  TIMEFRAME,
} from '../constants/common'
import { getIntradayChartConfig } from '../controllers/renderUtils'
import { ISettings } from '../models/chart_settings/interfaces'
import { getSettings } from '../models/settings'
import { isRedesignEnabled } from '../utils'
import { isForexFuturesCryptoPage } from './chart'

export function getChartLayoutSizeConfig(
  layoutConfig: Pick<
    RootChartConfigObject,
    'idea' | 'barWidth' | 'barMargin' | 'charts' | 'specificChartFunctionality' | 'editable' | 'width' | 'theme'
  >,
  p = TIMEFRAME.d,
  chartSettings?: ISettings
) {
  const { clientWidth } = document.body
  const { premarket, aftermarket, dateRange } = layoutConfig.charts[0]
  let barWidth = 3
  let barMargin = 1
  let timeframeMinutes = 0
  const isForexFuturesCrypto = isForexFuturesCryptoPage(layoutConfig.specificChartFunctionality!)
  const isChartPage = layoutConfig.specificChartFunctionality === SpecificChartFunctionality.chartPage
  const isQuoteOrQuoteFinancials = [
    SpecificChartFunctionality.quotePage,
    SpecificChartFunctionality.quoteFinancials,
  ].includes(layoutConfig.specificChartFunctionality!)
  const isFullWidthChart =
    isQuoteOrQuoteFinancials && [TIMEFRAME.i1, TIMEFRAME.i2, TIMEFRAME.i3, TIMEFRAME.i5].includes(p)

  if (isChartPage) {
    return {
      width: layoutConfig.width,
      barWidth,
      barMargin,
    }
  }
  const { ChartSettings } = chartSettings ?? getSettings({ ...layoutConfig, theme: layoutConfig.theme! })
  const chartXPadding = ChartSettings.left.width + ChartSettings.right.width

  // This matches .fv-container.has-responsive-padding
  const hasCompactMargin = window.innerWidth < 1280
  const pageMargin = isQuoteOrQuoteFinancials && hasCompactMargin ? 10 : 20
  const maxChartWidth = clientWidth - 2 * pageMargin

  // layoutConfig.editable = drawings on/off
  // The reason for x2 is that the same space has to be on the other side so the chart is always centered
  const toolbarWidth = layoutConfig.editable ? 2 * LEGACY_VERTICAL_TOOLBAR_WIDTH_WITH_OFFSET : 0

  let width =
    clientWidth < LEGACY_HORIZONTAL_TOOLBAR_BREAK_POINT || isFullWidthChart
      ? maxChartWidth
      : Math.min(maxChartWidth - toolbarWidth, isForexFuturesCrypto ? 1425 : isRedesignEnabled() ? 1393 : 1385)

  switch (p) {
    case TIMEFRAME.i1:
      timeframeMinutes = 1
      barWidth = 1
      const barWidthWithMargin = barWidth + 2 + barWidth // 2 is bar left & right border
      const mainMarketWidth = barWidthWithMargin * 390 // 390 bars in 1min timeframe in one day
      if (premarket || aftermarket || maxChartWidth < mainMarketWidth + chartXPadding) {
        barMargin = 0
      }
      break
    case TIMEFRAME.i2:
      timeframeMinutes = 2
      break
    case TIMEFRAME.i3:
      timeframeMinutes = 3
      if (premarket && aftermarket && clientWidth <= 1505) {
        barWidth = 2
      }
      break
    case TIMEFRAME.i5:
      timeframeMinutes = 5
      barWidth = 5
      barMargin = clientWidth >= 1100 ? 2 : 1
      break
    default:
      break
  }

  if (isQuoteOrQuoteFinancials) {
    switch (p) {
      case TIMEFRAME.i1:
      case TIMEFRAME.i2:
      case TIMEFRAME.i3:
      case TIMEFRAME.i5:
        const commonProps = {
          timeframeMinutes,
          barWidthWithMargin: barWidth + 2 + barMargin,
          aftermarketMinutesLength: aftermarket
            ? Math.ceil((aftermarket - MARKET_END_MINUTES) / timeframeMinutes) * timeframeMinutes
            : 0,
          premarketMinutesLength: premarket
            ? Math.ceil((MARKET_START_MINUTES - premarket) / timeframeMinutes) * timeframeMinutes
            : 0,
        }
        width =
          getIntradayChartConfig({
            ...commonProps,
            maxWidth: width - chartXPadding,
            canCropChart: !dateRange,
          }).chartWidth + chartXPadding
        break
    }
  }

  // If idea chart or FFC chart use bar configs from layout
  if (layoutConfig.idea || isForexFuturesCrypto) {
    barWidth = layoutConfig.barWidth!
    barMargin = layoutConfig.barMargin!
  }

  return {
    width,
    barWidth,
    barMargin,
    isLegacyToolbarHorizontal:
      clientWidth < LEGACY_HORIZONTAL_TOOLBAR_BREAK_POINT || clientWidth < width + toolbarWidth + 2 * pageMargin,
  }
}
